import React, { useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";
import "../css/Calculator.css";

const Calculator = () => {
  const [principal, setPrincipal] = useState("");
  const [rate, setRate] = useState("");
  const [months, setMonths] = useState(6);
  const [emi, setEmi] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [summary, setSummary] = useState({ totalPayment: 0, totalInterest: 0 });

  const COLORS = ["#3b82f6", "#10b981"];

  const calculateEmi = (e) => {
    e.preventDefault();
    if (!principal || !rate || !months) {
      alert("Please fill all fields");
      return;
    }

    const P = parseFloat(principal);
    const r = parseFloat(rate) / 12 / 100; // monthly interest rate
    const n = parseInt(months);

    const EMI = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    setEmi(EMI.toFixed(2));

    const totalPayment = EMI * n;
    const totalInterest = totalPayment - P;

    setSummary({
      totalPayment: totalPayment.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
    });

    setChartData([
      { name: "Principal", value: P },
      { name: "Interest", value: totalInterest },
    ]);

    let balance = P;
    const monthlyArray = [];
    for (let i = 1; i <= n; i++) {
      const interestPayment = balance * r;
      const principalPayment = EMI - interestPayment;
      balance -= principalPayment;
      monthlyArray.push({
        month: i,
        Principal: parseFloat(principalPayment.toFixed(2)),
        Interest: parseFloat(interestPayment.toFixed(2)),
      });
    }
    setMonthlyData(monthlyArray);
  };

  return (
    <div className="emi-container">
      <h2 className="emi-title">💰 Modern EMI Calculator</h2>
      <form className="emi-form" onSubmit={calculateEmi}>
        <div className="form-group">
          <label>Principal Amount</label>
          <input
            type="number"
            placeholder="Enter Principal Amount"
            value={principal}
            onChange={(e) => setPrincipal(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Annual Interest Rate (%)</label>
          <input
            type="number"
            placeholder="Enter Interest Rate"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Tenure (Months)</label>
          <select value={months} onChange={(e) => setMonths(e.target.value)}>
            {Array.from({ length: 60 }, (_, i) => 6 + i).map((m) => (
              <option key={m} value={m}>
                {m} months
              </option>
            ))}
          </select>
        </div>

        <button type="submit" className="calculate-btn">
          Calculate EMI
        </button>
      </form>

      {emi && (
        <div className="emi-result-container">
          <div className="emi-summary">
            <h3>📊 EMI Result</h3>
            <p>
              <strong>Monthly EMI:</strong> ₹{emi}
            </p>
            <p>
              <strong>Total Payment:</strong> ₹{summary.totalPayment}
            </p>
            <p>
              <strong>Total Interest:</strong> ₹{summary.totalInterest}
            </p>
          </div>

          <div className="charts-container">
            {/* Pie Chart */}
            <div className="chart-card">
              <h4>Principal vs Interest</h4>
              <PieChart width={320} height={320}>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  label={(entry) =>
                    `${entry.name}: ₹${entry.value.toFixed(0)}`
                  }
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(val) => `₹${val.toFixed(2)}`} />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </div>

            {/* Bar Chart */}
            <div className="chart-card">
              <h4>Monthly Payment Breakdown</h4>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(val) => `₹${val.toFixed(2)}`} />
                  <Legend verticalAlign="top" />
                  <Bar dataKey="Principal" stackId="a" fill="#3b82f6" />
                  <Bar dataKey="Interest" stackId="a" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calculator;
